import { formatInTimeZone } from "date-fns-tz";
import moment from "moment-timezone";

const DATE_TIME_FORMAT = "dd MMM yyyy, h:mm a";
const DATE_FORMAT = "dd MMM yyyy";

/** The viewer's IANA timezone, falling back to `"UTC"` when `Intl` is unavailable (e.g. SSR edge). */
export function getLocalTimezone(): string {
	return typeof Intl === "undefined"
		? "UTC"
		: Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * UTC offset for `date` in `timeZone`, e.g. `"UTC+12:00"`, `"UTC+5:30"`, `"UTC"`.
 * Uses `xxx` so minutes are always present (`+12:00`); strips a leading hour zero for single-digit
 * offsets (`+05:30` → `UTC+5:30`).
 */
export function formatUtcOffset(date: Date, timeZone: string): string {
	const offset = formatInTimeZone(date, timeZone, "xxx");
	if (offset === "+00:00" || offset === "-00:00") {
		return "UTC";
	}
	return `UTC${offset.replace(/([+-])0(\d)/, "$1$2")}`;
}

/**
 * Collapse DST-specific abbreviations to their short generic form when the zone uses both
 * standard and daylight variants (e.g. `NZST`/`NZDT` → `NZT`, `AEST`/`AEDT` → `AET`,
 * `EST`/`EDT` → `ET`). Leaves abbreviations that have no pair alone (e.g. `IST`).
 */
function toGenericAbbreviation(abbreviation: string, timeZone: string): string {
	const match = abbreviation.match(/^([A-Z]+)[SD]T$/);
	if (!match) {
		return abbreviation;
	}

	const stem = match[1];
	const zone = moment.tz.zone(timeZone);
	if (!zone) {
		return abbreviation;
	}

	const abbrs = new Set(zone.abbrs);
	if (abbrs.has(`${stem}ST`) && abbrs.has(`${stem}DT`)) {
		return `${stem}T`;
	}

	return abbreviation;
}

/**
 * Short generic zone abbreviation for `timeZone` at `date` (e.g. `"NZT"`, `"AET"`, `"ET"`).
 * Resolved via `moment-timezone`, which reads the IANA tz database directly for reliable regional
 * names instead of the `GMT+…` offsets `Intl` returns for many zones.
 * Returns `""` when the IANA data only exposes a numeric offset (e.g. `"+12"`, `"-04"`).
 */
export function getTimezoneAbbreviation(date: Date, timeZone: string): string {
	const abbreviation = moment.tz(date, timeZone).zoneAbbr();
	if (abbreviation && !/^(GMT|UTC|[+-]\d)/i.test(abbreviation)) {
		return toGenericAbbreviation(abbreviation, timeZone);
	}
	return "";
}

/**
 * Compact zone label such as `"NZT (UTC+12:00)"`. When no named abbreviation is available, returns
 * just the offset (`"UTC+5:30"`).
 */
export function formatTimezoneLabel(
	timeZone: string,
	date: Date = new Date(),
): string {
	const abbreviation = getTimezoneAbbreviation(date, timeZone);
	const offset = formatUtcOffset(date, timeZone);
	return abbreviation ? `${abbreviation} (${offset})` : offset;
}

/** Wall clock in `timeZone` (date-fns `a` → uppercase `AM`/`PM`). */
export function formatWallClock(
	date: Date,
	timeZone: string,
	{ showTime = true }: { showTime?: boolean } = {},
): string {
	return formatInTimeZone(
		date,
		timeZone,
		showTime ? DATE_TIME_FORMAT : DATE_FORMAT,
	);
}

/**
 * Wall-clock instant with zone label, e.g. `"30 Jun 2026, 5:00 PM NZT (UTC+12:00)"`.
 * When `showTime` is false, omits the time: `"30 Jun 2026 NZT (UTC+12:00)"`.
 */
export function formatZonedDateTimeLabel(
	date: Date,
	timeZone: string,
	{ showTime = true }: { showTime?: boolean } = {},
): string {
	return `${formatWallClock(date, timeZone, { showTime })} ${formatTimezoneLabel(timeZone, date)}`;
}
